/**
 * EVENTSFLO-I18N-PHASE-2H-ADMIN-PORTAL-RUNTIME-LOOKUP-V1
 *
 * SuperAdmin-only runtime translation lookup PREVIEW.
 *
 * Safety:
 *   - Returns null for non-SuperAdmin.
 *   - Lookup is preview-only — English remains the live default.
 *   - There is NO "Enable runtime translations" control.
 *   - There is NO "Enable translated PDFs / emails" control.
 *   - The dry-run sample quote endpoint returns a structured
 *     render PLAN (JSON), never a real PDF binary, never persists.
 *   - Banner clearly states production_activation: false.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  FileText,
  Info,
  Play,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

async function authedFetch(path, token, options = {}) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  let body = null;
  try { body = await r.json(); } catch { body = null; }
  if (!r.ok) {
    const detail = body?.detail || body || { error: `HTTP ${r.status}` };
    const msg =
      typeof detail === "string"
        ? detail
        : detail.message || detail.error || `HTTP ${r.status}`;
    const err = new Error(msg);
    err.status = r.status;
    throw err;
  }
  return body;
}

function FlagBadge({ enabled }) {
  return (
    <Badge
      className={
        enabled
          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
          : "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30"
      }
    >
      {enabled ? "Enabled" : "Disabled"}
    </Badge>
  );
}

function StatusBadge({ status }) {
  const map = {
    hit: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
    miss: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
    cache_missing:
      "bg-rose-500/15 text-rose-300 border border-rose-500/30",
    namespace_missing:
      "bg-amber-500/15 text-amber-300 border border-amber-500/30",
    language_missing:
      "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  };
  return (
    <Badge
      className={map[status] || map.miss}
      data-testid={`i18n-runtime-lookup-status-${status}`}
    >
      {status}
    </Badge>
  );
}

export default function I18nRuntimePreviewCard() {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);

  // Lookup form state.
  const [namespace, setNamespace] = useState("pdf.quote");
  const [sourceKey, setSourceKey] = useState("title");
  const [sourceText, setSourceText] = useState("Quote");
  const [targetLanguage, setTargetLanguage] = useState("fr");
  const [lookupResult, setLookupResult] = useState(null);

  const [pdfPlan, setPdfPlan] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setStatusLoading(true);
    setStatusError(null);
    try {
      const body = await authedFetch(
        `/api/admin/i18n/runtime/status`, token,
      );
      setStatus(body);
    } catch (e) {
      setStatusError(e.message || "Status failed");
    } finally {
      setStatusLoading(false);
    }
  }, [isSuperAdmin, token]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runLookup = async () => {
    if (!namespace.trim() || !sourceKey.trim() || !sourceText.trim() || !targetLanguage.trim()) {
      toast.error("All fields are required.");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(
        `/api/admin/i18n/runtime/lookup-preview`, token,
        {
          method: "POST",
          body: JSON.stringify({
            namespace: namespace.trim(),
            source_key: sourceKey.trim(),
            source_text: sourceText.trim(),
            target_language: targetLanguage.trim(),
          }),
        },
      );
      setLookupResult(body);
      const s = body.lookup_result?.status;
      if (s === "hit") {
        toast.success(`Hit — resolved to "${body.lookup_result.text}"`);
      } else {
        toast.warning(`No translation (${s}) — fell back to English`);
      }
    } catch (e) {
      toast.error(e.message || "Lookup failed");
    } finally {
      setActionLoading(false);
    }
  };

  const runPdfPreview = async () => {
    setActionLoading(true);
    try {
      const body = await authedFetch(
        `/api/admin/i18n/pdf-preview/sample-quote`, token,
        {
          method: "POST",
          body: JSON.stringify({
            target_language: targetLanguage.trim() || "fr",
            sample_mode: true,
          }),
        },
      );
      setPdfPlan(body);
      toast.success(
        `Dry-run render plan generated for sample quote (${body.target_language})`,
      );
    } catch (e) {
      toast.error(e.message || "Dry-run failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <Card data-testid="i18n-runtime-preview-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Admin i18n runtime preview
        </CardTitle>
        <p className="text-xs text-zinc-400 mt-1">
          SuperAdmin-only. Runtime lookup is{" "}
          <strong className="text-zinc-300">preview-only</strong> in
          this phase. English remains the live production language.
          Missing translations fall back to English.
        </p>
      </CardHeader>
      <CardContent>
        {/* Production-unchanged banner */}
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200 flex items-start gap-2"
          data-testid="i18n-runtime-production-unchanged-note"
        >
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5" />
          <span>
            production_activation: <strong>false</strong>. Live UI,
            PDFs and emails are unchanged. No translation is wired
            into the runtime app shell by this card.
          </span>
        </div>

        {/* Status & flags */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-runtime-status-section"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" /> Runtime status
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadStatus}
              disabled={statusLoading}
              data-testid="i18n-runtime-status-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          {statusError ? (
            <div className="text-xs text-rose-300" data-testid="i18n-runtime-status-error">
              {statusError}
            </div>
          ) : !status ? (
            <div className="text-xs text-zinc-500">Loading status…</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-zinc-400 text-xs">Runtime lookup</div>
                <div className="mt-0.5" data-testid="i18n-runtime-flag-runtime">
                  <FlagBadge enabled={status.runtime_lookup_enabled} />
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">Admin preview</div>
                <div className="mt-0.5" data-testid="i18n-runtime-flag-admin">
                  <FlagBadge enabled={status.admin_preview_enabled} />
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">PDF preview</div>
                <div className="mt-0.5" data-testid="i18n-runtime-flag-pdf">
                  <FlagBadge enabled={status.pdf_preview_enabled} />
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">Production activation</div>
                <div className="mt-0.5">
                  <Badge
                    className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    data-testid="i18n-runtime-activation-badge"
                  >
                    No
                  </Badge>
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">Cache exists</div>
                <div className="text-zinc-300" data-testid="i18n-runtime-cache-exists">
                  {String(status.cache_exists)}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">Checksum</div>
                <div
                  className="font-mono text-xs text-zinc-300"
                  data-testid="i18n-runtime-checksum"
                >
                  {status.checksum ? `${status.checksum.slice(0, 12)}…` : "—"}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">Namespaces</div>
                <div className="text-zinc-300 font-mono text-xs">
                  {(status.namespaces || []).join(", ") || "—"}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">Languages</div>
                <div className="text-zinc-300 font-mono text-xs">
                  {(status.languages || []).join(", ") || "—"}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Lookup preview */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-runtime-lookup-section"
        >
          <div className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <Play className="w-3.5 h-3.5" /> Single lookup preview
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-zinc-400" htmlFor="i18n-runtime-namespace">
                namespace
              </Label>
              <Input
                id="i18n-runtime-namespace"
                value={namespace}
                onChange={(e) => setNamespace(e.target.value)}
                data-testid="i18n-runtime-namespace-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400" htmlFor="i18n-runtime-source-key">
                source_key
              </Label>
              <Input
                id="i18n-runtime-source-key"
                value={sourceKey}
                onChange={(e) => setSourceKey(e.target.value)}
                data-testid="i18n-runtime-source-key-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400" htmlFor="i18n-runtime-source-text">
                source text (English)
              </Label>
              <Input
                id="i18n-runtime-source-text"
                value={sourceText}
                onChange={(e) => setSourceText(e.target.value)}
                data-testid="i18n-runtime-source-text-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">target language</Label>
              <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                <SelectTrigger data-testid="i18n-runtime-target-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">fr</SelectItem>
                  <SelectItem value="it">it</SelectItem>
                  <SelectItem value="es">es</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <Button
              onClick={runLookup}
              disabled={actionLoading}
              data-testid="i18n-runtime-lookup-btn"
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Preview lookup
            </Button>
          </div>
          {lookupResult ? (
            <div
              className="mt-2 rounded border border-zinc-700/40 bg-zinc-900/40 p-2 text-xs grid grid-cols-2 md:grid-cols-4 gap-2"
              data-testid="i18n-runtime-lookup-result"
            >
              <div>
                <div className="text-zinc-400">status</div>
                <div className="mt-0.5">
                  <StatusBadge status={lookupResult.lookup_result.status} />
                </div>
              </div>
              <div>
                <div className="text-zinc-400">resolved text</div>
                <div
                  className="text-zinc-300"
                  data-testid="i18n-runtime-lookup-resolved-text"
                >
                  {lookupResult.lookup_result.text}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">resolved language</div>
                <div className="text-zinc-300 font-mono">
                  {lookupResult.lookup_result.language}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">fallback used</div>
                <div className="text-zinc-300" data-testid="i18n-runtime-lookup-fallback-used">
                  {String(lookupResult.lookup_result.fallback_used)}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Dry-run sample quote PDF */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-runtime-pdf-section"
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" /> Dry-run sample translated quote PDF
              </div>
              <p
                className="text-xs text-zinc-500 mt-1"
                data-testid="i18n-runtime-pdf-helper"
              >
                Dry-run only. Returns a structured render plan
                (JSON), never a real PDF binary. No quote, invoice,
                project, client or email record is changed. Nothing
                is persisted.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={runPdfPreview}
              disabled={actionLoading}
              data-testid="i18n-runtime-pdf-preview-btn"
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Preview sample translated quote PDF
            </Button>
          </div>
          {pdfPlan ? (
            <div
              className="mt-2 rounded border border-zinc-700/40 bg-zinc-900/40 p-2 text-xs"
              data-testid="i18n-runtime-pdf-plan-result"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                <div>
                  <div className="text-zinc-400">dry_run</div>
                  <div className="text-zinc-300">{String(pdfPlan.dry_run)}</div>
                </div>
                <div>
                  <div className="text-zinc-400">activation</div>
                  <div className="text-zinc-300">
                    {String(pdfPlan.production_activation)}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">target language</div>
                  <div className="text-zinc-300 font-mono">
                    {pdfPlan.target_language}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">sample id</div>
                  <div className="text-zinc-300 font-mono">
                    {pdfPlan.sample_quote?.sample_id}
                  </div>
                </div>
              </div>
              <table
                className="w-full text-xs border border-zinc-700/40 rounded"
                data-testid="i18n-runtime-pdf-plan-labels-table"
              >
                <thead className="bg-zinc-800/60 text-zinc-400">
                  <tr>
                    <th className="text-left px-2 py-1">label key</th>
                    <th className="text-left px-2 py-1">English</th>
                    <th className="text-left px-2 py-1">resolved</th>
                    <th className="text-left px-2 py-1">status</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(pdfPlan.resolved_labels || {}).map(
                    ([k, v]) => (
                      <tr
                        key={k}
                        className="odd:bg-zinc-900/30"
                        data-testid={`i18n-runtime-pdf-plan-label-${k}`}
                      >
                        <td className="px-2 py-1 font-mono text-zinc-300">{k}</td>
                        <td className="px-2 py-1 text-zinc-300">{v.english}</td>
                        <td className="px-2 py-1 text-emerald-300">{v.resolved}</td>
                        <td className="px-2 py-1">
                          <StatusBadge status={v.status} />
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
              <div
                className="mt-2 text-amber-300"
                data-testid="i18n-runtime-pdf-warning"
              >
                {pdfPlan.warning}
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
